import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
  Box,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import UploadFile from "components/_refactor/dropzone";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import { columnTextField } from "utils/tables-utils";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  listCategories,
  createCategory,
  editCategory,
  deleteCategory,
} from "../services/franchise-marketing-api";
import { ACCEPT_ICONS } from "../constants";

const STATUS_OPTIONS = [
  { value: true, label: "Ativo" },
  { value: false, label: "Inativo" },
];

const EMPTY_FORM = {
  nome: "",
  descricao: "",
  ativo: STATUS_OPTIONS[0],
  ordem: "",
  cor: "#5D5FEF",
};

export default function DialogCategorias({ open, onClose, onSaved }) {
  const snackbar = useSnackbar();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [iconFiles, setIconFiles] = useState([]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const result = await listCategories(false);
      setCategories(result);
    } catch (error) {
      console.error(error);
      snackbar.showMessage(
        error?.message || "Falha ao carregar categorias.",
        { severity: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadCategories();
    } else {
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      setIconFiles([]);
    }
  }, [open]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = (row) => {
    setEditingId(row.IdFranquiaMarketingCategoria);
    setForm({
      nome: row.Nome,
      descricao: row.Descricao || "",
      ativo:
        STATUS_OPTIONS.find((s) => s.value === row.Ativo) || STATUS_OPTIONS[0],
      ordem: String(row.Ordem),
      cor: row.Cor || "#5D5FEF",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nome?.trim()) {
      snackbar.showMessage("Nome da categoria é obrigatório.", {
        severity: "error",
      });
      return;
    }

    setSaving(true);

    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao,
        ordem: Number(form.ordem) || 0,
        ativo: form.ativo?.value ?? true,
        cor: form.cor || "#5D5FEF",
      };

      if (editingId) {
        await editCategory({
          ...payload,
          idFranquiaMarketingCategoria: editingId,
        });
        snackbar.showMessage("Categoria atualizada com sucesso.", {
          severity: "success",
        });
      } else {
        await createCategory(payload);
        snackbar.showMessage("Categoria criada com sucesso.", {
          severity: "success",
        });
      }

      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      setIconFiles([]);
      await loadCategories();
      onSaved?.();
    } catch (error) {
      console.error(error);
      snackbar.showMessage(
        error?.message || "Falha ao salvar categoria.",
        { severity: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Excluir a categoria "${row.Nome}"?`)) return;

    try {
      await deleteCategory(row.IdFranquiaMarketingCategoria);
      snackbar.showMessage("Categoria excluída.", { severity: "success" });
      await loadCategories();
      onSaved?.();
    } catch (error) {
      snackbar.showMessage(error?.message || "Falha ao excluir categoria.", {
        severity: "error",
      });
    }
  };

  const columnModel = [
    columnTextField({
      dataField: "Nome",
      text: "Categoria",
      align: "left",
      formatter: (_, row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: row.Cor || "#5D5FEF",
              marginRight: 8,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {row.Nome}
          </Typography>
        </Box>
      ),
    }),
    columnTextField({
      dataField: "Descricao",
      text: "Descrição",
      align: "left",
    }),
    columnTextField({
      dataField: "Ativo",
      text: "Status",
      align: "center",
      formatter: (_, row) => (
        <Box
          style={{
            display: "inline-block",
            padding: "4px 10px",
            borderRadius: 6,
            backgroundColor: row.Ativo ? "#0AA83633" : "#f5f5f5",
            color: row.Ativo ? "#0AA836" : "#616161",
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          {row.Ativo ? "Ativo" : "Inativo"}
        </Box>
      ),
    }),
    columnTextField({ dataField: "Ordem", text: "Ordem", align: "center" }),
    columnTextField({
      dataField: "acoes",
      text: "Ações",
      align: "center",
      formatter: (_, row) => (
        <Box display="flex" justifyContent="center" style={{ gap: 4 }}>
          <IconButton
            size="small"
            onClick={() => handleEdit(row)}
            aria-label="Editar"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(row)}
            aria-label="Excluir"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    }),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography
          variant="h4"
          component="span"
          style={{ color: themeColorsDesingSystem.text.primary }}
        >
          Gestão de Categorias
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!showForm ? (
          <>
            <Box display="flex" justifyContent="flex-end" marginBottom={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => {
                  setEditingId(null);
                  setForm(EMPTY_FORM);
                  setShowForm(true);
                }}
              >
                Nova categoria
              </Button>
            </Box>
            <ToolkitTable
              keyField="IdFranquiaMarketingCategoria"
              dataTable={categories}
              columnModel={columnModel}
              notFoundTitle={
                loading
                  ? "Carregando categorias..."
                  : "Nenhuma categoria cadastrada."
              }
              pagination={{
                options: [10, 25],
                defaultOption: 10,
                showTotal: true,
              }}
            />
          </>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="h4"
                style={{ color: themeColorsDesingSystem.text.primary }}
              >
                {editingId ? "Editar Categoria" : "Nova Categoria"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <InputText
                label="Nome da categoria"
                value={form.nome}
                onChange={(e) => updateForm("nome", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <InputText
                label="Descrição"
                value={form.descricao}
                onChange={(e) => updateForm("descricao", e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputText
                label="Cor (hex)"
                value={form.cor}
                onChange={(e) => updateForm("cor", e.target.value)}
                placeholder="#5D5FEF"
              />
              <Box marginTop={1}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Ícone (opcional)
                </Typography>
                <UploadFile
                  message="Upload de ícone (opcional)"
                  accept={ACCEPT_ICONS}
                  onChangeFiles={setIconFiles}
                  resetKey={`${open}-${editingId || "new"}-icon`}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <AutoCompleteField
                    label="Status"
                    value={form.ativo}
                    onChange={(v) => updateForm("ativo", v)}
                    options={STATUS_OPTIONS}
                    optionLabelKey="label"
                    optionIdKey="value"
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputText
                    label="Ordem"
                    type="number"
                    value={form.ordem}
                    onChange={(e) => updateForm("ordem", e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        {showForm ? (
          <>
            <Button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setForm(EMPTY_FORM);
              }}
              variant="outlined"
              disabled={saving}
            >
              Voltar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={saving}
            >
              Salvar categoria
            </Button>
          </>
        ) : (
          <Button onClick={onClose} variant="outlined">
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
